import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * ErrorBoundary — catches unhandled rendering errors in its subtree so a crash
 * degrades gracefully instead of white-screening the app.
 *
 * Layered usage across the app:
 *   - ROOT (App.js): no props -> `page` variant, full-screen catch-all.
 *   - ROUTE (layout Outlets): variant="section" + resetKeys={[pathname]} so a
 *     single page crash keeps the app chrome (nav/header) and auto-recovers when
 *     the user navigates elsewhere.
 *   - WIDGET (e.g. floating AI widget): fallback={() => null} so a non-critical
 *     widget failure silently disappears without affecting the page.
 *
 * Props (all optional):
 *   variant   'page' (default, full-screen) | 'section' (inline card)
 *   resetKeys array — when any value changes (shallow), a caught error is cleared
 *   fallback  node | (error, reset) => node — custom fallback; overrides variant
 *   name      string — labels the boundary in logs/telemetry
 *   onError   (error, errorInfo) => void
 *   onReset   () => void
 */
function shallowArrayEqual(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const name = this.props.name || 'app';
    // eslint-disable-next-line no-console
    console.error(`ErrorBoundary[${name}] caught an error:`, error, errorInfo);
    // Best-effort telemetry (PostHog is loaded as an inline script in index.html).
    try {
      window.posthog?.capture?.('frontend_error', {
        boundary: name,
        message: error?.message,
        component_stack: errorInfo?.componentStack,
      });
    } catch (_e) {
      /* never let logging throw */
    }
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Auto-recover when a reset key changes (e.g. route navigation) so the user
    // isn't stuck staring at a stale error after moving to a different view.
    if (
      this.state.hasError &&
      this.props.resetKeys &&
      !shallowArrayEqual(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  handleReload = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      const { fallback, variant = 'page' } = this.props;
      if (typeof fallback === 'function') return fallback(this.state.error, this.reset);
      if (fallback !== undefined) return fallback;
      return variant === 'section' ? (
        <SectionFallback error={this.state.error} onReset={this.reset} />
      ) : (
        <PageFallback error={this.state.error} onReload={this.handleReload} />
      );
    }
    return this.props.children;
  }
}

const isDev = process.env.NODE_ENV !== 'production';

// Dev-only detail block so engineers see the message without a console dive;
// hidden in production where users should never see a stack.
const ErrorDetails = ({ error }) =>
  isDev && error ? (
    <details className="mt-4 w-full text-left">
      <summary className="cursor-pointer text-xs text-slate-400">Error details</summary>
      <pre className="mt-2 max-h-40 overflow-auto rounded bg-slate-50 p-3 text-[11px] text-red-600 whitespace-pre-wrap">
        {String(error?.stack || error?.message || error)}
      </pre>
    </details>
  ) : null;

/** Full-screen fallback — the catastrophic / root catch-all. */
const PageFallback = ({ error, onReload }) => (
  <div
    className="min-h-screen flex items-center justify-center bg-slate-50"
    data-testid="error-boundary-page"
  >
    <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="w-6 h-6 text-red-600" />
      </div>
      <h2 className="text-lg font-semibold text-slate-900 mb-2">Something went wrong</h2>
      <p className="text-sm text-slate-500 mb-6">
        An unexpected error occurred. Please try reloading the page.
      </p>
      <button
        onClick={onReload}
        className="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
        data-testid="error-boundary-reload"
      >
        Return to Dashboard
      </button>
      <ErrorDetails error={error} />
    </div>
  </div>
);

/** Inline fallback — for route/section subtrees, keeps the surrounding chrome. */
const SectionFallback = ({ error, onReset }) => (
  <div
    className="flex flex-col items-center justify-center min-h-[40vh] py-12 text-center"
    data-testid="error-boundary-section"
  >
    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
      <AlertTriangle className="w-6 h-6 text-red-600" />
    </div>
    <h2 className="text-base font-semibold text-slate-900 mb-1">
      This section failed to load
    </h2>
    <p className="text-sm text-slate-500 max-w-sm mb-6">
      Something went wrong rendering this view. You can try again, or navigate
      elsewhere and come back.
    </p>
    <div className="flex items-center gap-2">
      <button
        onClick={onReset}
        className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
        data-testid="error-boundary-retry"
      >
        <RotateCcw className="w-4 h-4" />
        Try again
      </button>
      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center justify-center rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
      >
        Reload page
      </button>
    </div>
    <ErrorDetails error={error} />
  </div>
);

export default ErrorBoundary;
